import { Economy } from "@highmarch/app";
import { bannerSceneUrl } from "./shopsArt";
import type { ShopSlotView, ShopView } from "@highmarch/app";
import { BalanceData, GearItem, ShopCurrency, ShopId } from "@highmarch/domain";
import { bindSeg, btnHtml, bump, fmtCompact, tickNumber } from "../kit";
import { toast } from "../toast";
import { resourceIconUrl, uiUrl } from "../portraits";
import { rewardChest } from "../chestReveal";
import { RARITY_LABEL } from "../gearUi";
import { BannerTheme, currencyLineHtml, heroHtml, iconTabsHtml, lockChipHtml, priceButtonHtml, secHtml, sheetHeadHtml, tickTimers, timerChipHtml } from "../components";
import { confirmPurchase, econCue, purchasePop } from "../hub/purchaseFx";
import { fmtCountdown } from "../deals/dealsModel";
import { chooseGearSlot } from "../deals/pickSheet";
import { openItemPopup } from "./itemPopup";
import { slotPopup } from "./slotPopup";
import { showActiveTab } from "./tabStrip";
import { ShopsLink, shopsLink } from "./shopsLink";
import { RefreshButton, SKINS_TAB, SUPPLIES_TAB, ShopTab, ShopTabId, SlotTile, TROPHY_TAB, initialShopTab, refreshButton, refusalText, shopTabs, shortHint, slotTile } from "./shopsModel";
import { STOREFRONT_ICON } from "./shopsArt";
import { currencyIconUrl } from "../hub/econArt";
import { economyLink } from "../hub/economyLink";
import { shopModel } from "../hub/shopModel";
import { ShopActions } from "../hub/shopActions";
import { packsHtml, type ShopDeps } from "../hub/shopSheet";
import { mountSuppliesShelf } from "../supplies/suppliesShelf";
import { mountBoostsShelf } from "../boosts/boostsShelf";
import { mountPetTreatsShelf } from "../pets/treatShelf";
import { mountSkinsShop } from "../hub/skinShelves";
import { slotHtml } from "./shopsTiles";
import "../styles/shops.css";
import { SHOP_SKIN } from "../shopSkin";
import { trophyLink } from "../trophy/trophyLink";
import { TROPHY_TAB_ICON, TrophyHallPage } from "../trophy/trophyHallPage";
import { notEnoughGems } from "../hub/getGold";

/**
 * The Shops sheet (docs/SHOP.md §9.5; Kingshot's Mystery / Arena / VIP shops, redrawn in the game's own look): a hub
 * sheet under the top bar — the header with the keeper's portrait and the tab's currencies, the Merchant · Event · VIP
 * icon tabs, then the shop's page: its keeper's hero (the restock countdown; the Merchant's Refresh) over a 3-column
 * grid of slots. The Merchant's restock is 00:00 UTC; the Event and VIP shops' Monday 00:00 UTC, the featured row on
 * top; the VIP shop's locked entries show their VIP level. A tap on a slot confirms what it is and costs (with the odds
 * of a random item) and buys it as shown; a chest opens with the chest reveal. MP-23: the Trophy Hall is the fourth tab
 * (ui/trophy/trophyHallPage.ts draws its page) when the feature is in the build. Opened only by a tap in the hub (More →
 * Shops, the hub's Shops button, Market → Merchant, the VIP page's Shop button): never from a level,
 * the end screen or a timer.
 */

/** How often the countdowns tick. */
const TICK_MS = 1000;
/** How long a bought tile's glow plays before the page redraws it. */
const REVEAL_MS = 700;
/** Under this many seconds a restock countdown's clock turns red. */
const URGENT_S = 3600;
/** The currencies each tab's header line shows: what that page spends (the Merchant's restock gems too). */
const TAB_CURRENCIES: Readonly<Record<ShopTabId, readonly ("gems" | "marks" | "trophies" | "gold")[]>> = {
  merchant: ["marks", "gems"], supplies: ["gems", "gold"], event: ["marks"], vip: ["gems"], trophy: ["trophies"], skins: ["gems", "marks"],
};
/** Each shop's bright banner (shop redesign 2026-10-19): its daylight backdrop, Captain Bram's pose and the prop beside him. */
const SCENE_OF: Readonly<Record<ShopId, string>> = { merchant: "merchant", event: "event", vip: "vip" };
const BANNER: Readonly<Record<ShopId, { theme: BannerTheme; cast: string; prop: string; title: string }>> = {
  merchant: { theme: "market", cast: "captain/bram_pointing@3x.png", prop: "chests/chest_rare_closed.png", title: "Mystery Merchant" },
  event: { theme: "camp", cast: "captain/bram_thumbsup@3x.png", prop: "icons/shop_tab_event.png", title: "Event Shop" },
  vip: { theme: "royal", cast: "captain/bram_pointing@3x.png", prop: "icons/vip_crest_gold.png", title: "VIP Shop" },
};

/** Opens the Shops sheet on `tab` (the first open shop when null), or returns null outside the hub. */
export function openShops(tab: ShopTabId | null = null): HTMLElement | null {
  const l = shopsLink();
  if (!l || !l.hub) return null;                   // the hub only: never a level or the end screen
  l.hub.parent.querySelectorAll(":scope > .sheet.shops, :scope > .sheet.shophub").forEach((s) => s.remove());
  return new ShopsScreen(l, tab).el;
}

class ShopsScreen {
  readonly el: HTMLDivElement;
  private tab: ShopTabId;
  private timer = 0;
  private busy = false;
  private readonly trophy: TrophyHallPage | null;

  constructor(private readonly l: ShopsLink, tab: ShopTabId | null) {
    const tl = trophyLink();
    this.trophy = tl ? new TrophyHallPage(tl, () => this.parent, () => { if (this.el.isConnected) this.render(); }) : null;
    this.tab = initialShopTab(this.tabs(), tab);
    this.el = document.createElement("div");
    this.el.className = `sheet panel shops shophub shop ${SHOP_SKIN}`;
    this.render();
    l.hub!.parent.append(this.el);
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  private get balance(): BalanceData { return this.l.catalog.balance; }
  private get parent(): HTMLElement { return this.l.hub?.parent ?? this.el.parentElement ?? document.body; }
  private tabs(): ShopTab[] { const tl = trophyLink(); return shopTabs(this.l.shops, this.balance, tl && this.trophy ? { open: tl.hall.open, icon: TROPHY_TAB_ICON } : null); }
  /** The shop on show (the Merchant while the Trophy Hall shows: its taps never reach the shop rules). */
  private get shop(): ShopId { return this.tab === "merchant" || this.tab === "event" || this.tab === "vip" ? this.tab : "merchant"; }
  private view(shop: ShopId = this.shop): ShopView { return this.l.shops.view(shop, this.l.now()); }

  render(): void {
    const now = this.l.now(), tabs = this.tabs(), t = tabs.find((x) => x.id === this.tab)!;
    const scroller = this.el.querySelector<HTMLElement>(".sscroll");
    const keep = scroller && this.el.dataset.tab === this.tab ? scroller.scrollTop : 0;
    const stripX = this.el.querySelector<HTMLElement>(".k-itabs")?.scrollLeft ?? null;
    this.el.dataset.tab = this.tab;
    const w = this.l.catalog.economy.wallet, tp = this.tab === TROPHY_TAB ? this.trophy : null;
    // Shop redesign (2026-10-19): one header for every tab — the storefront, "Shop" and the currencies this tab spends.
    const line = currencyLineHtml(TAB_CURRENCIES[this.tab].map((c) => ({
      key: c, icon: c === "trophies" ? uiUrl(TROPHY_TAB_ICON) : currencyIconUrl(c), amount: fmtCompact(c === "trophies" ? w.trophies : c === "gold" ? this.l.catalog.profile.gold : w.balance(c)),
    })));
    const eventDot = this.l.shops.badge(now), merchantDot = this.l.shops.merchantFree(now);   // §20: free → red dot
    const suppliesDot = new Economy(this.l.catalog).freePackDot(now);
    // a closed shop's tab keeps its place (its teaser answers the tap) and wears a small lock
    const itabs = iconTabsHtml(tabs.map((x) => ({ key: x.id, label: x.title, icon: uiUrl(x.icon), dot: x.id === "event" ? eventDot : x.id === "merchant" ? merchantDot : x.id === SUPPLIES_TAB ? suppliesDot : 0, locked: !x.open })), this.tab, "Shop");
    const head = sheetHeadHtml({ title: "Shop", sub: line, portrait: uiUrl(STOREFRONT_ICON), tint: "navy" });
    const body = tp ? tp.bodyHtml()
      : this.tab === SUPPLIES_TAB ? `<section class="spage supplies"><div class="sgoods"></div></section>`
      : this.tab === SKINS_TAB ? `<section class="spage skins">${heroHtml({ art: "", title: "Skins", className: "shero skins", bright: { theme: "royal", cast: uiUrl("captain/bram_pointing@3x.png"), prop: uiUrl("icons/skins@3x.png"), scene: bannerSceneUrl("skins") } })}<div class="sskins"></div></section>`
      : t.open ? this.pageHtml(this.view(), now) : this.teaserHtml(t, this.view());
    this.el.innerHTML = `${head}<div class="s3tabs">${itabs}</div><div class="sscroll">${body}</div>`;
    const ns = this.el.querySelector<HTMLElement>(".sscroll");
    if (ns) ns.scrollTop = keep;
    if (this.tab === SUPPLIES_TAB) this.mountSupplies(now);
    if (this.tab === SKINS_TAB) this.mountSkins();
    this.bind();
    // the lit tab in view: the strip scrolls sideways (from where the finger left it), never the sheet
    if (stripX !== null) { const nt = this.el.querySelector<HTMLElement>(".k-itabs"); if (nt) nt.scrollLeft = stripX; }
    showActiveTab(this.el, stripX !== null);
  }

  /** The gem goods: gold packs (the free one of the day first), speed-ups, battle supplies and Pet Treats. */
  private mountSupplies(now: number): void {
    const host = this.el.querySelector<HTMLElement>(".sgoods"), d = this.goodsDeps();
    if (!host || !d) return;
    const m = shopModel(d.catalog, now);
    // Shop = spending currency, Store = real money (owner 2026-10-21): gold packs for gems live here, the free one first
    host.innerHTML = heroHtml({ art: "", title: "Supplies", className: "shero supplies",
      bright: { theme: "gold", cast: uiUrl("captain/bram_thumbsup@3x.png"), prop: resourceIconUrl("gold"), scene: bannerSceneUrl("gold") } })
      + packsHtml("Gold", "coin", "", m.resourcePacks);
    mountBoostsShelf(host, d);   // owner 2026-10-21: the battle boosts, after the gold
    host.insertAdjacentHTML("beforeend", packsHtml("Speed-ups", "clock", "", m.speedUps));
    new ShopActions(d, () => { if (this.el.isConnected) this.render(); }).bind(host, m);
    mountSuppliesShelf(host, d);
    mountPetTreatsShelf(host, d);
  }

  /** The skins for gems and for Marks (the shelves the Gems and Marks pages carried). */
  private mountSkins(): void {
    const host = this.el.querySelector<HTMLElement>(".sskins");
    if (!host) return;
    // every skin, by what it dresses; a tap previews it (owner 2026-10-20: the Shop has the skins, no "All skins" button)
    mountSkinsShop(host, this.parent, () => { if (this.el.isConnected) this.render(); });
  }

  /** What the gem goods' buttons need (the economy feature's link: the hub, the store, the save). */
  private goodsDeps(): ShopDeps | null {
    const e = economyLink(), hub = e?.hub ?? this.l.hub;
    return e && hub ? { hub, catalog: e.catalog, now: e.now, purchases: e.purchases, storeKind: e.storeKind } : null;
  }

  // ------------------------------------------------------------------ markup

  private tiles(v: ShopView): SlotTile[] {
    return v.slots.map((s) => slotTile(this.balance, this.l.shops.level, s));
  }

  /** The restock countdown chip. */
  private timerChip(shop: ShopId, endsAt: number, now: number): string {
    const left = endsAt - now;
    return timerChipHtml(fmtCountdown(left), { label: shop === "merchant" ? "" : "Refreshes in", endsAt, urgent: left < URGENT_S });
  }

  /** The shop's bright banner: its keeper (Captain Bram), the restock countdown, and (the Merchant) Restock. */
  private heroHtml(shop: ShopId, endsAt: number, now: number, side = ""): string {
    const b = BANNER[shop];
    return heroHtml({ art: "", title: b.title, timer: this.timerChip(shop, endsAt, now), side, className: `shero ${shop}`, bright: { theme: b.theme, cast: uiUrl(b.cast), prop: uiUrl(b.prop), scene: bannerSceneUrl(SCENE_OF[shop]) } });
  }

  private pageHtml(v: ShopView, now: number): string {
    const tiles = this.tiles(v);
    const grid = (list: readonly SlotTile[], cls = "") => `<div class="k-grid3 sslots${cls ? ` ${cls}` : ""}">${list.map(slotHtml).join("")}</div>`;
    if (v.shop === "merchant") {
      const rb = refreshButton(v);
      return `<section class="spage merchant">${this.heroHtml("merchant", v.endsAt, now, refreshHtml(rb, this.l.catalog.economy.wallet.gems))}<div class="s3shelf">${grid(tiles)}</div></section>`;
    }
    const featured = tiles.filter((t) => t.featured), rest = tiles.filter((t) => !t.featured);
    // Kingshot's Arena and VIP shops: a refresh ribbon over the shelf, the featured stock first.
    // owner 2026-10-21: every Shop tab has its banner (the restock countdown rides on it, as on the Merchant's)
    return `<section class="spage ${v.shop}">${this.heroHtml(v.shop, v.endsAt, now)}
      <div class="s3shelf">${featured.length > 0 ? `${secHtml("Featured")}${grid(featured, "feat")}${secHtml("This week")}` : ""}${grid(rest)}</div></section>`;
  }

  /** A closed tab: its banner, why it is closed, and (VIP) what it will sell. */
  private teaserHtml(t: ShopTab, v: ShopView): string {
    const id = v.shop, b = BANNER[id];
    const hero = heroHtml({ art: "", title: b.title, timer: lockChipHtml(id === "vip" ? "VIP 1" : "Not yet"), className: `shero ${id}`, bright: { theme: b.theme, cast: uiUrl(b.cast), prop: uiUrl(b.prop), scene: bannerSceneUrl(SCENE_OF[id]) } });
    const preview = id === "vip" ? `<div class="s3shelf">${secHtml("What it sells")}<div class="k-grid3 sslots preview">${this.tiles(v).map(slotHtml).join("")}</div></div>` : "";
    return `<section class="spage ${id} closed">${hero}<p class="steaser">${t.teaser}</p>${preview}</section>`;
  }

  // ------------------------------------------------------------------ events

  private bind(): void {
    const q = <T extends HTMLElement>(s: string) => [...this.el.querySelectorAll<T>(s)];
    this.el.querySelector<HTMLButtonElement>(".sheethead .close")!.onclick = () => this.close();
    bindSeg(this.el, (k) => { if (k !== this.tab) { this.tab = k as ShopTabId; econCue("tap"); this.render(); } });
    if (this.tab === TROPHY_TAB) { this.trophy?.bind(this.el); return; }   // its own switch and tiles (after the tabs)
    if (this.tab === SUPPLIES_TAB || this.tab === SKINS_TAB) return;       // their shelves bind their own buttons
    if (this.el.querySelector(".spage.closed")) {
      // a closed shop's preview: what each item is, its lock stamped where BUY would be
      for (const b of q<HTMLElement>(".stile")) b.onclick = () => {
        const s = this.view().slots.find((x) => x.index === Number(b.dataset.i));
        if (s) void openItemPopup(this.parent, slotPopup(this.balance, this.l.shops.level, s, this.shop === "vip" ? "VIP 1" : "Not yet"));
      };
      return;
    }
    // the whole slot is the tap target (its gold price button included: the click bubbles to the slot)
    for (const b of q<HTMLElement>(".stile")) b.onclick = () => void this.buy(Number(b.dataset.i), b);
    this.el.querySelector<HTMLButtonElement>(".srefresh")?.addEventListener("click", () => void this.refresh());
  }

  close(): void { window.clearInterval(this.timer); this.el.remove(); this.parent.querySelectorAll(":scope > .schest").forEach((x) => x.remove()); }

  /** The countdowns tick each second; a restock (00:00 UTC, Monday) redraws the page with the new stock. */
  private tick(): void {
    if (!this.el.isConnected) { window.clearInterval(this.timer); return; }
    if (tickTimers(this.el, this.l.now(), fmtCountdown) && !this.busy) this.render();
  }

  // ------------------------------------------------------------------ buying

  private async buy(index: number, tile: HTMLElement): Promise<void> {
    if (this.busy) return;
    const s = this.view().slots.find((x) => x.index === index);
    if (!s) { this.render(); return; }
    // owner 2026-10-24: every tile opens its popup, even sold out or locked (the reason stamped where BUY would be)
    const stamp = s.blocked === "sold out" ? "Sold out" : s.locked ? `VIP ${s.vipLevel}` : "";
    if (s.blocked && !stamp && s.blocked !== "not enough") { toast(this.parent, refusalText(s.blocked, s.currency)); return; }
    // one purchase at a time: a second tap while the slot chooser or the popup is up does nothing
    this.busy = true;
    const gearSlots: number[] = [];
    let ok = false;
    try {
      ok = await openItemPopup(this.parent, slotPopup(this.balance, this.l.shops.level, s, stamp));
      if (ok && !s.blocked && s.item?.kind === "gear" && s.item.slot === "pick") {
        const g = await chooseGearSlot(this.parent, this.balance, s.item.rarity, -1);
        if (g === null) return;
        gearSlots.push(g);
      }
    } finally {
      this.busy = false;
    }
    if (!ok) return;
    if (s.blocked === "not enough") { this.short(s); return; }
    const w = this.l.catalog.economy.wallet, before = w.balance(s.currency);
    const r = this.l.shops.buy(s, this.l.now(), "hub", gearSlots);
    if (typeof r === "string") { toast(this.parent, refusalText(r, s.currency)); this.render(); return; }
    econCue(s.currency === "gems" ? "buy" : "trade");
    this.l.changed();
    const name = slotTile(this.balance, this.l.shops.level, s).tile.name;
    if (r.opened.length > 0) {
      // the chest reveal plays over the screen, which redraws under it at once (the tile sold, the pill down)
      this.render();
      this.tickPill(s.currency, before, w.balance(s.currency));
      this.reveal(name, r.opened);
      return;
    }
    purchasePop(tile);
    this.tickPill(s.currency, before, w.balance(s.currency));
    toast(this.parent, r.gear ? `${RARITY_LABEL[r.gear.rarity]} ${this.balance.gear.slots[r.gear.slot].name} is in your Gear stash` : r.granted.gear.length > 0 ? `${name} is in your Gear stash, bound to you` : `${name} is yours`);
    this.busy = true;                                    // the tile's glow plays out before the page redraws it
    window.setTimeout(() => { this.busy = false; if (this.el.isConnected) this.render(); }, REVEAL_MS);
  }

  /** The header's pill for a currency. */
  private pill(c: ShopCurrency): HTMLElement | null { return this.el.querySelector<HTMLElement>(`.k-curline .k-cl[data-cur="${c}"]`); }

  /** The currency pill in the header rolls to the new balance. */
  private tickPill(c: ShopCurrency, from: number, to: number): void {
    const n = this.pill(c)?.querySelector<HTMLElement>(":scope > b");
    if (!n || from === to) return;
    tickNumber(n, from, to, 450, fmtCompact);
    bump(n.parentElement, 1.12, 200);
  }

  /** BUY with too little: the gems sheet (how many are missing), or Marks' toast and the pill's bump. */
  private short(s: ShopSlotView): void {
    if (s.currency === "gems") {
      const need = (typeof s.price === "number" ? s.price : 0) - this.l.catalog.economy.wallet.gems;
      void notEnoughGems(this.parent, Math.max(1, need), this.el);
      return;
    }
    toast(this.parent, refusalText("not enough", s.currency));
    bump(this.pill(s.currency), 1.15, 220);
  }

  /** Each chest the purchase paid, opened with the chest reveal over the page (one after another). */
  private reveal(name: string, opened: readonly { chest: { rarity: number; gold: number; speedUpMinutes: number }; gear: readonly GearItem[] }[]): void {
    const next = (i: number): void => {
      const o = opened[i];
      if (!o || !this.el.isConnected) return;
      // over the screen, not in it: the battle chest's 3D chest and slot machine (M30 #10)
      const c = o.chest;
      rewardChest(this.parent, this.balance, c.rarity, { gear: [...o.gear], speedUpMinutes: c.speedUpMinutes, gold: c.gold }, `${name}${opened.length > 1 ? ` · ${i + 1}/${opened.length}` : ""}`, () => next(i + 1));
    };
    next(0);
  }

  private async refresh(): Promise<void> {
    if (this.busy) return;
    const v = this.view("merchant"), rb = refreshButton(v), w = this.l.catalog.economy.wallet;
    if (rb.state === "none") { toast(this.parent, refusalText("no refresh left today", "gems")); return; }
    if (rb.state === "gems") {
      if (w.gems < rb.gems) { toast(this.parent, shortHint("gems")); bump(this.pill("gems"), 1.15, 220); return; }
      const ladder = this.balance.shops.merchant.refreshGems, paid = this.l.shops.state.merchant.paidUsed;
      const after = ladder[paid + 1];
      const ok = await confirmPurchase(this.parent, { shop: true, title: "RESTOCK", currency: "gems", price: rb.gems, confirm: "RESTOCK", className: "sconfirm",
        body: `<p>The Merchant brings out ${v.slots.length} new goods, all in stock.</p><p class="snext">${after ? `The next restock today: ${after} gems` : "The last restock today"}</p>` });
      if (!ok) return;
    }
    const before = w.gems;
    const r = this.l.shops.refresh(this.l.now());
    if (typeof r === "string") { toast(this.parent, refusalText(r, "gems")); this.render(); return; }
    econCue(r > 0 ? "buy" : "tap");
    this.l.changed();
    this.render();
    this.el.querySelector(".spage.merchant .sslots")?.classList.add("fresh");
    this.tickPill("gems", before, w.gems);
    toast(this.parent, r > 0 ? `New goods for ${r} gems` : "New goods: a free restock");
  }
}

/** The Merchant's Restock in his banner (owner 2026-10-19: "Refresh" read oddly): green when free, the gem price (red when short), or none left today. */
function refreshHtml(rb: RefreshButton, gems: number): string {
  if (rb.state === "none") return btnHtml("Back tomorrow", "dark", { className: "srefresh s-none", disabled: true });
  if (rb.state === "free") return btnHtml("Free Restock", "blue", { className: "srefresh s-free green" });
  return priceButtonHtml({ currency: "gems", amount: rb.label, label: "Restock", short: gems < rb.gems, className: "srefresh s-gems" });
}

/** One slot's markup (the tests read it): the well, "Left: n", the gold price, Sold out or the VIP lock. */
export const tileCardHtml = slotHtml;
